/**
 * Pure state helper for the MarketplaceStatusCard (M3 — plan 255).
 *
 * Derives the discriminated-union card state from `{ connection, offerCounts,
 * pendingRequest }` — no React dependencies, fully unit-testable.
 *
 * DTO field names mirror the M6-B contract exactly (tech-spec
 * §"M6-B endpoint contract") so M8 can swap fixtures for live data with no
 * shape change.
 *
 * Load-bearing invariants enforced here:
 *   1. `charging` and `held` map to IDENTICAL state kinds (finalizing /
 *      finalizing-escape) — the buyer cannot distinguish them.
 *   2. `pro_name`/`pro_contact` only enter the output state when
 *      `status === "active"` — the connected-payoff card is the only
 *      surface that can ever render contact channels.
 *   3. The escape affordance is gated SOLELY on `escape_eligible` — no
 *      client-side time math.
 *   4. Payment vocabulary (`fee_cents`, `discount_cents`,
 *      `amount_charged_cents`) is NEVER read or copied by this helper.
 */

import type { PinnedOffer } from "./threads-api";
import type { OfferItemLanes } from "./marketplace-offers-api";

// ── M6-B DTO shapes (pinned) ──────────────────────────────────────────────────

export type BuyerConnectionStatus =
  | "charging"
  | "held"
  | "active"
  | "ended"
  | "abandoned"
  | "canceled_by_buyer";

const BUYER_CONNECTION_STATUSES = new Set<string>([
  "charging",
  "held",
  "active",
  "ended",
  "abandoned",
  "canceled_by_buyer",
]);

/**
 * Parse a `?connectionStatus=` query value (the DEV preview suppression
 * seam shared by the marketplace preview pages). Absent or invalid -> null
 * (not suppressed).
 */
export function parseConnectionStatusParam(
  raw: string | null,
): BuyerConnectionStatus | null {
  return raw !== null && BUYER_CONNECTION_STATUSES.has(raw)
    ? (raw as BuyerConnectionStatus)
    : null;
}

/**
 * Check-in card copy variant (M8-C cycle 5, spec §3, artboard 3c):
 * "close_claim" for the agent's "did you close?" ask (and every rung of its
 * day-0/7/30 reminder ladder — D11, a re-send of the SAME ask), "light" for
 * the periodic active-stage "how's it going?" ask.
 */
export type CheckinCardVariant = "close_claim" | "light";

/**
 * Check-in card DTO — the buyer-facing connection DTO's `checkin` block
 * (M8-C cycle 5). Deliberately three fields only: never
 * `review_request_armed_at`, never the relationship stage itself. `pending`
 * is the only field a caller strictly needs to decide whether to render the
 * card; `variant`/`asked_at` drive copy once it does.
 */
export interface CheckinCardDTO {
  pending: boolean;
  variant?: CheckinCardVariant;
  /** ISO timestamp of the most recently fired checkin/ladder ask. */
  asked_at?: string;
}

/**
 * Plan 462 M5: the buyer connection's `no_contact_exit` block. The SERVER
 * decides `eligible` live on every read (it can disappear on the next load),
 * so the client renders from `eligible` alone and never derives it from
 * `eligible_since` or any date. `eligible_since` carries no outreach
 * timestamp or source, only the moment the option opened.
 */
export interface NoContactExit {
  eligible: boolean;
  eligible_since: string | null;
}

/**
 * Defensive parse of the wire block: anything that is not an object with a
 * literal `eligible: true` is "not eligible" (fail closed). `eligible_since`
 * survives only as a non-empty string.
 */
export function parseNoContactExit(raw: unknown): NoContactExit {
  if (typeof raw !== "object" || raw === null) {
    return { eligible: false, eligible_since: null };
  }
  const block = raw as { eligible?: unknown; eligible_since?: unknown };
  return {
    eligible: block.eligible === true,
    eligible_since:
      typeof block.eligible_since === "string" && block.eligible_since !== ""
        ? block.eligible_since
        : null,
  };
}

/**
 * Buyer-facing connection DTO from `GET /api/marketplace/connection?role=`.
 * Field names are load-bearing — match the Go JSON tags exactly.
 *
 * Buyer FE reads ONLY: `id`, `role`, `status`, `escape_eligible`,
 * `pro_name` (at active), `pro_contact` (at active), `checkin` (at active),
 * `no_contact_exit` (at active).
 * Payment fields (`fee_cents`, `discount_cents`, `amount_charged_cents`) are
 * intentionally absent from this type — they must never reach the buyer FE.
 */
export interface BuyerConnectionDTO {
  id: string;
  role: string;
  status: BuyerConnectionStatus;
  /** Server-computed: true once 72h past creation AND not yet charged.
   *  Additive B field (b-addendum-escape-eligible). */
  escape_eligible: boolean;
  /** Present ONLY when status === "active". Absent on the wire for all
   *  other statuses — enforce at the render layer too (defense-in-depth). */
  pro_name?: string;
  pro_photo_url?: string;
  /** Reveal-gated (active only) — the pro's outward-facing supply id, used
   *  to submit a review from the review-email deep link. Never buyer PII. */
  pro_user_id?: string;
  /** Present ONLY when status === "active". Same omission discipline. */
  pro_contact?: string;
  /**
   * The frozen accepted-offer/standing-offer snapshot (same shape as the
   * thread's pinned card, task:hub-bindings-offer-first-class). Item
   * magnitudes only — a rebate percentage, cash-lane dollar figures, perks —
   * NEVER a connection payment field (fee_cents/discount_cents/
   * amount_charged_cents remain forbidden reads). Optional-degrades: absent
   * when no offer resolves, present at any connection status (buyer already
   * agreed to these terms before the connection formed).
   */
  accepted_offer?: PinnedOffer;
  /** Present ONLY when status === "active" AND the backend has a check-in
   *  card resolver wired (M8-C cycle 5). Absent otherwise — never a
   *  fabricated `{pending:false}` block. */
  checkin?: CheckinCardDTO;
  /**
   * D8 rev 4 (plan 291 M8-B) consumer projection of `crm_contacts.stage` —
   * the raw enum ("connected"/"agreement"/"active"/"under_contract"/
   * "closed"), never display copy (resolve via
   * `lib/relationship-stage-display.ts`'s `stageDisplayLabel`/`stageTone`).
   * Rides the SAME reveal gate as `pro_name` server-side (absent pre-reveal
   * and whenever the SILL-427 active-link carve-out denies projection — an
   * ended link omits it too). Plan 435 M4 (mfh-connected-stack) is the
   * first Home surface to read this; the backend has emitted it on this
   * wire shape since plan 291 — purely additive on the FE type, no backend
   * change.
   */
  relationship_stage?: string;
  /** D12 derived "quiet for 30+ days" overlay — same reveal/active-link
   *  gate as `relationship_stage`. Computed at read time, never stored;
   *  false (the zero value) omits the field on the wire. */
  dormant?: boolean;
  /** Plan 462 M5: always emitted by the server (never omitted) and always
   *  present after `normalizeConnection`; optional on the type only so
   *  fixtures that predate it keep compiling. Absent reads as ineligible. */
  no_contact_exit?: NoContactExit;
}

export type ConnectionRequestStatus =
  | "pending_pro"
  | "confirmed"
  | "declined"
  | "expired"
  | "withdrawn_by_buyer";

/**
 * The frozen standing-offer terms a pending request carries (plan 435 M0,
 * additive) — exactly the shape `offerKeyFacts` (`@sill/shared`) renders
 * from. Mirrors the Go `connectionRequestOfferDTO` JSON tags.
 */
export interface ConnectionRequestOfferDTO {
  commission_pct?: number;
  items: OfferItemLanes;
}

/**
 * Connection request DTO from the `pending_request` field of
 * `GET /api/marketplace/connection?role=` (plan 435 M0 — the request has no
 * `marketplace_connections` row of its own, so it rides the same endpoint
 * as `connection`, additive alongside it).
 */
export interface ConnectionRequestDTO {
  id: string;
  role: string;
  buyer_tier: string;
  status: ConnectionRequestStatus;
  decline_reason?: string;
  /** ISO timestamp — practical field included with responses so the UI can
   *  render "sent N hours ago". Optional to stay forward-compatible. */
  created_at?: string;
  /** The 72h buyer-consent deadline (plan 435 M0, additive). */
  expires_at?: string;
  /** Additive (plan 435 M0): the buyer already chose this specific pro
   *  before accepting their standing offer, so naming them back is not a
   *  leak — unlike the pre-charge connection flow's reveal gate. */
  pro_display_name?: string;
  /** Additive (plan 435 M0): the frozen offer the buyer saw at accept
   *  time. Absent for a pre-M0 row or a row with no resolvable snapshot. */
  offer?: ConnectionRequestOfferDTO;
}

// ── Discriminated-union card states ───────────────────────────────────────────

/**
 * No connection (or a terminal connection: ended / abandoned / canceled).
 * Renders offer counts + newest-offer teaser → Your Offers.
 *
 * M8 wiring note: when the live `getOffersInbox` response includes the newest
 * offer, map its agent name, terms summary (e.g. "2% rebate + $500"), and
 * ISO timestamp into these three optional fields. No state-shape change needed.
 */
export interface PublishedOffersState {
  kind: "published-offers";
  madeForYou: number;
  availableToYou: number;
  /** Display name of the newest offer agent for the teaser, if available. */
  newestOfferAgentName?: string;
  /** Human-readable offer incentive summary (e.g. "0.9% rebate + $500").
   *  Offer-incentive vocabulary — distinct from connection payment fields
   *  (`fee_cents` etc.) which are forbidden here. Optional-degrades. */
  newestOfferTermsSummary?: string;
  /** ISO timestamp of the newest offer. Rendered via `formatSentAgo` as
   *  "· Nh ago". Optional-degrades — absent → time omitted from teaser. */
  newestOfferCreatedAt?: string;
}

/**
 * Connection `charging` or `held`, `escape_eligible=false`.
 * Renders "Connecting you — finalizing" with NO cancel affordance.
 * BOTH statuses map to this identical state — the buyer never learns which.
 */
export interface FinalizingState {
  kind: "finalizing";
  connectionId: string;
  /** Agent display name from session (pre-active, DTO omits it).
   *  Absent → card uses "your agent" neutral copy. */
  agentDisplayName?: string;
}

/**
 * Connection `charging` or `held`, `escape_eligible=true` (day 3+).
 * Renders "Taking longer than expected" tone + "Cancel and keep browsing".
 * Same charging≡held equivalence as FinalizingState.
 */
export interface FinalizingEscapeState {
  kind: "finalizing-escape";
  connectionId: string;
  agentDisplayName?: string;
}

/**
 * Connection `active`.
 * Renders the full agent contact card — the ONLY state that ever renders
 * `pro_name`/`pro_contact`. Contact channels are gated on `active` here
 * (defense-in-depth, mirroring B's handler-level guard).
 */
export interface ConnectedPayoffState {
  kind: "connected-payoff";
  connectionId: string;
  /** From DTO `pro_name` — only present at active. */
  proName: string;
  proPhotoUrl?: string;
  /** From DTO `pro_contact` — only present at active. */
  proContact: string;
  /** From DTO `pro_user_id` — only present at active (same reveal gate as
   *  `proName`/`proContact`). The Your Team "Message" deep-link shape
   *  (`/threads?pro=<proUserId>`) reads this, never a connection id. */
  proUserId?: string;
  /** Optional human-readable summary of agreed terms (from the accepted
   *  offer, passed via session state). M8 wires this; M3 fixture provides it. */
  agreedTermsSummary?: string;
  /**
   * The frozen accepted-offer snapshot, first-class on the connected card
   * (task:hub-bindings-offer-first-class). Same `PinnedOffer` shape the
   * thread's collapsible summary renders — item magnitudes only, never a
   * connection payment field. Absent when the DTO carries no offer.
   */
  acceptedOffer?: PinnedOffer;
  /** From DTO `relationship_stage` — plan 435 M4, `ConnectedStack`'s
   *  `RelationshipStageChip`. Absent whenever the server's own reveal/
   *  active-link gate omits it from the DTO. */
  relationshipStage?: string;
}

/**
 * Pending standing request (`status: "pending_pro"`).
 * Renders "Waiting for [Agent] to confirm" + withdraw affordance.
 * Reads from `GET /api/marketplace/requests` — persistent across reloads.
 */
export interface WaitingAgentState {
  kind: "waiting-agent";
  requestId: string;
  /** ISO timestamp for "sent N hours ago" display. */
  createdAt?: string;
  /** Agent display name from the accept flow (not on the request DTO).
   *  Absent → card uses "the agent" neutral copy. */
  agentDisplayName?: string;
}

export type MarketplaceCardState =
  | PublishedOffersState
  | FinalizingState
  | FinalizingEscapeState
  | ConnectedPayoffState
  | WaitingAgentState;

// ── Status helpers ────────────────────────────────────────────────────────────

const TERMINAL_STATUSES = new Set<BuyerConnectionStatus>([
  "ended",
  "abandoned",
  "canceled_by_buyer",
]);

export function isTerminalConnection(status: BuyerConnectionStatus): boolean {
  return TERMINAL_STATUSES.has(status);
}

export function isInFlightOrActive(status: BuyerConnectionStatus): boolean {
  return (
    status === "charging" || status === "held" || status === "active"
  );
}

// ── Inputs & derivation ───────────────────────────────────────────────────────

export interface DeriveMarketplaceCardStateInput {
  /** null = no connection for this role (or not yet loaded). */
  connection: BuyerConnectionDTO | null;
  offerCounts: { madeForYou: number; availableToYou: number };
  /** Optional display name for the newest-offer teaser. */
  newestOfferAgentName?: string;
  /** null/undefined = no pending request for this role. */
  pendingRequest?: ConnectionRequestDTO | null;
  /** Optional agent display name from the accept-flow session (pre-active). */
  agentDisplayName?: string;
}

/**
 * Derives the status-card state from connection + offer counts + request.
 *
 * Precedence (highest → lowest):
 *   1. connected-payoff   — status === "active"
 *   2. finalizing-escape  — status ∈ {charging, held} AND escape_eligible
 *   3. finalizing         — status ∈ {charging, held} AND !escape_eligible
 *   4. waiting-agent      — pending_pro request, no in-flight/active connection
 *   5. published-offers   — all other cases
 *
 * Safety invariants:
 *   • `charging` and `held` both map to identical state kinds (no branching).
 *   • `pro_name`/`pro_contact` only enter the output at `active`.
 *   • Payment vocabulary is never read from the connection DTO.
 */
export function deriveMarketplaceCardState(
  input: DeriveMarketplaceCardStateInput,
): MarketplaceCardState {
  const {
    connection,
    offerCounts,
    newestOfferAgentName,
    pendingRequest,
    agentDisplayName,
  } = input;

  // Priority 1: active → connected-payoff (only state with contact reveal)
  if (connection !== null && connection.status === "active") {
    return {
      kind: "connected-payoff",
      connectionId: connection.id,
      // Reveal discipline: read pro_name/pro_contact/pro_user_id ONLY at active
      proName: connection.pro_name ?? "",
      proPhotoUrl: connection.pro_photo_url,
      proContact: connection.pro_contact ?? "",
      proUserId: connection.pro_user_id,
      acceptedOffer: connection.accepted_offer,
      relationshipStage: connection.relationship_stage,
    };
  }

  // Priority 2 + 3: charging OR held → finalizing (escape gate determines variant)
  // Both statuses map to the IDENTICAL kind — the buyer cannot distinguish them.
  if (
    connection !== null &&
    (connection.status === "charging" || connection.status === "held")
  ) {
    if (connection.escape_eligible) {
      return {
        kind: "finalizing-escape",
        connectionId: connection.id,
        agentDisplayName,
      };
    }
    return {
      kind: "finalizing",
      connectionId: connection.id,
      agentDisplayName,
    };
  }

  // Priority 4: pending standing request (connection is null or terminal)
  if (pendingRequest !== null && pendingRequest !== undefined && pendingRequest.status === "pending_pro") {
    return {
      kind: "waiting-agent",
      requestId: pendingRequest.id,
      createdAt: pendingRequest.created_at,
      agentDisplayName,
    };
  }

  // Priority 5: published-offers (null/terminal connection, no pending request)
  return {
    kind: "published-offers",
    madeForYou: offerCounts.madeForYou,
    availableToYou: offerCounts.availableToYou,
    newestOfferAgentName,
  };
}

// ── Display utilities ─────────────────────────────────────────────────────────

/**
 * Formats an ISO timestamp as "N hours ago" for the waiting-agent card.
 * Returns "just now" for < 1 hour, "N hours ago" otherwise.
 * Safe to call in tests with mocked Date.now.
 */
export function formatSentAgo(isoString: string, now = Date.now()): string {
  const diffMs = now - new Date(isoString).getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  if (hours < 1) return "just now";
  if (hours === 1) return "1 hour ago";
  return `${hours.toString()} hours ago`;
}
